// Display · TabCount
//
// Numeric chip rendered beside an AntD <Tabs> item label.
// Active state highlights the chip with primaryBg + primary text from TONE.blue.
// Inactive state uses the grey soft-bg ramp for a muted look.
//
// Atom — no events, no configurable affordances. Output and Actions interfaces
// are dropped per the @ds atom convention (Rule 3 / ds-engineer.md).
//
// All visual values come from `@ds/tokens/palette`.

import {
  CHIP,
  FONT_SIZE,
  FONT_WEIGHT,
  LETTER_SPACING,
  LINE_HEIGHT,
  RADIUS,
  SPACE,
  TONE,
} from "@ds/tokens/palette";

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface TabCountInput {
  /** Numeric count to display inside the chip. */
  value: number;
  /**
   * Whether the parent tab is currently active.
   * Active uses TONE.blue ramp; inactive uses TONE.grey soft ramp.
   */
  active?: boolean;
  /** `data-testid` applied to the root <span>. */
  testId?: string;
}

// ─── Component contract ──────────────────────────────────────────────────
//
// Atom: no Output, no Actions.

export type TabCountProps = TabCountInput;

// ─── Implementation ──────────────────────────────────────────────────────

export function TabCount({ value, active = false, testId }: TabCountProps) {
  const t = active ? TONE.blue : TONE.grey;

  return (
    <span
      data-testid={testId}
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
        minWidth:        CHIP.size,
        height:          CHIP.size,
        padding:         "0 6px",          // 6px tab-count horizontal pad — between SPACE.xs (4) and SPACE.sm (8)
        marginLeft:      SPACE.xs,
        background:      t.softBg,
        color:           t.text,
        border:          `1px solid ${active ? t.border : "transparent"}`,
        borderRadius:    RADIUS.pill,
        fontSize:        FONT_SIZE.label,
        fontWeight:      FONT_WEIGHT.medium,
        lineHeight:      LINE_HEIGHT.none,
        letterSpacing:   LETTER_SPACING.chip,
        whiteSpace:      "nowrap",
      }}
    >
      {value}
    </span>
  );
}
